import { useRef, useState, type FocusEvent, type PointerEvent, type ReactNode } from 'react';
import { Pause, Play, SkipBack, SkipForward, Volume1, Volume2, VolumeX, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useGlobalAudioPlayback } from '@/audio/GlobalAudioContext';
import { useGlobalAudioScrub } from '@/audio/useGlobalAudioScrub';
import { useGlobalAudioVolumeSlider, type GlobalAudioVolumeSlider } from '@/audio/useGlobalAudioVolumeSlider';
import { formatAudioClock } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { cn } from '../cn';
import { DURATION, EASE_OUT } from '../motion';
import { NewDesignScope } from '../NewDesignScope';
import { Tooltip } from '../ui/tooltip';

export type AudioBarScrub = ReturnType<typeof useGlobalAudioScrub>;

/**
 * Сколько плеер занимает снизу окна: свёрнутый плеер 44 px (кнопка ▶ 36 + py-1) и его отступ от низа 20 px (bottom-5 ниже).
 * Раскрытый плеер выше, но запас под него не растёт: раскрывают его ненадолго, под мышью.
 */
export const AUDIO_BAR_ROOM_PX = 64;

/**
 * Запас в конце прокрутки, пока плеер на экране: последний блок доезжает до плеера, а не уходит под него.
 * Ставится последним в прокрутке, вне промежутков (gap) её содержимого. Подписан на плеер сам — прокрутку вокруг не перерисовывает.
 */
export function AudioBarSpace() {
  const { track } = useGlobalAudioPlayback();
  return track ? <div aria-hidden="true" data-audio-bar-space="" style={{ height: AUDIO_BAR_ROOM_PX }} /> : null;
}

/** Общий плеер приложения (старый и новый дизайн): внизу по центру, появляется, когда что-то играет. */
export function AudioBar() {
  const { track } = useGlobalAudioPlayback();
  const reduced = useReducedMotion();
  return (
    // Над содержимым страниц, под окнами и тостами обновления (AppUpdate — выше 1100)
    <NewDesignScope className="dark nd:pointer-events-none nd:fixed nd:inset-x-0 nd:bottom-5 nd:z-[1100] nd:flex nd:justify-center nd:px-4">
      <AnimatePresence>
        {track ? (
          <motion.div
            key="audio-bar"
            initial={{ opacity: 0, y: reduced ? 0 : 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduced ? 0 : 16 }}
            transition={{ duration: DURATION.fast, ease: EASE_OUT }}
            className="nd:flex nd:max-w-full nd:justify-center"
          >
            <ConnectedAudioBar />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </NewDesignScope>
  );
}

/** Плеер с данными общего плеера; часы (перемотка) подписываются только пока он на экране */
function ConnectedAudioBar() {
  const { track, isPlaying, toggle, stop, playPrevious, playNext, canPlayPrevious, canPlayNext } = useGlobalAudioPlayback();
  const scrub = useGlobalAudioScrub();
  const volume = useGlobalAudioVolumeSlider();
  if (!track) return null;
  return (
    <AudioBarView
      title={track.title}
      isPlaying={isPlaying}
      // Соседние треки есть только у Фонотеки (файлы проекта играют по одному)
      skips={track.source === 'fonoteka' ? { canPrevious: canPlayPrevious, canNext: canPlayNext, onPrevious: playPrevious, onNext: playNext } : null}
      onToggle={toggle}
      onClose={stop}
      scrub={scrub}
      volume={volume}
    />
  );
}

const ICON_BUTTON =
  'nd:grid nd:size-8 nd:shrink-0 nd:place-items-center nd:rounded-full nd:press nd:press-icon nd:focus-ring nd:transition-[background-color,color,opacity] nd:duration-(--press-duration) nd:hover:bg-foreground/10 nd:disabled:pointer-events-none nd:disabled:opacity-35 nd:[&_svg]:size-4';
const ICON_MUTED = 'nd:text-muted-foreground nd:hover:text-foreground';
/**
 * Ползунки плеера — перемотка (горизонтальная) и громкость (вертикальная) — из одних деталей: цель для мыши 16 px,
 * видимая дорожка 4 px, под мышью и пока тянут — 6 px и белый бегунок 12 px. Группа scrub — у самого ползунка.
 */
const SLIDER =
  'nd:group/scrub nd:relative nd:flex nd:shrink-0 nd:cursor-pointer nd:touch-none nd:items-center nd:justify-center nd:rounded-full nd:outline-none nd:focus-visible:ring-2 nd:focus-visible:ring-foreground/25 nd:aria-disabled:cursor-default';
const SLIDER_TRACK = 'nd:relative nd:overflow-hidden nd:rounded-full nd:bg-input nd:duration-150';
const SLIDER_RANGE = 'nd:absolute nd:bg-primary';
const SLIDER_THUMB =
  'nd:absolute nd:size-3 nd:scale-0 nd:rounded-full nd:bg-white nd:shadow-md nd:transition-transform nd:duration-150 nd:group-hover/scrub:scale-100 nd:group-focus-visible/scrub:scale-100 nd:group-data-scrubbing/scrub:scale-100';

/** Раскрытие и сворачивание: строка перемотки выезжает вниз, ширина плеера не меняется */
const EXPAND = 'nd:duration-[260ms] nd:ease-(--ease-out-ui) nd:motion-reduce:transition-none';

export interface AudioBarViewProps {
  title: string;
  isPlaying: boolean;
  /** Соседние треки; null — кнопок ⏮ ⏭ нет */
  skips: { canPrevious: boolean; canNext: boolean; onPrevious: () => void; onNext: () => void } | null;
  onToggle: () => void;
  onClose: () => void;
  scrub: AudioBarScrub;
  volume: GlobalAudioVolumeSlider;
  /** Открыт с самого начала (истории); в жизни раскрывается наведением, фокусом с клавиатуры или нажатием на название */
  defaultExpanded?: boolean;
}

/**
 * Чёрная капсула (концепт dark-player 1.1.3): в покое — ▶, название, ⏮ ⏭, громкость, закрыть; под мышью, при фокусе
 * с клавиатуры, пока тянут перемотку или громкость — плавно вырастает вниз: выезжает перемотка от края до края со временем по бокам.
 * Ширина не меняется — 480 px.
 * На сенсорном экране мыши нет — раскрывается и сворачивается нажатием на название.
 */
export function AudioBarView({ title, isPlaying, skips, onToggle, onClose, scrub, volume, defaultExpanded = false }: AudioBarViewProps) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinned, setPinned] = useState(defaultExpanded);
  const lastPointer = useRef<string>('mouse');
  const expanded = hovered || focused || pinned || scrub.isScrubbing || volume.open;

  const onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') setHovered(true);
  };
  const onPointerLeave = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') setHovered(false);
  };
  // Раскрывает только фокус с клавиатуры: после нажатия мышью кнопка остаётся в фокусе, и плеер не свернулся бы
  const onFocus = (e: FocusEvent) => {
    if ((e.target as HTMLElement).matches(':focus-visible')) setFocused(true);
  };
  const onBlur = (e: FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
  };

  return (
    <div
      role="region"
      aria-label="Плеер"
      // Скругление 22 px = радиус кнопки ▶ (18) + отступ (4): в покое капсула, при раскрытии углы те же, кнопка сидит в углу ровно
      data-expanded={expanded ? '' : undefined}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={(e) => (lastPointer.current = e.pointerType)}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(
        'nd:pointer-events-auto nd:flex nd:w-[30rem] nd:max-w-full nd:flex-col nd:rounded-[1.375rem] nd:bg-card nd:py-1 nd:pr-2 nd:pl-1 nd:text-card-foreground nd:shadow-lg nd:shadow-black/25 nd:ring-1 nd:ring-foreground/10'
      )}
    >
      <div className="nd:flex nd:items-center nd:gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label={isPlaying ? `Пауза: ${title}` : `Воспроизвести: ${title}`}
          className="nd:grid nd:size-9 nd:shrink-0 nd:place-items-center nd:rounded-full nd:bg-primary nd:text-primary-foreground nd:press nd:press-icon nd:focus-ring nd:hover:bg-primary/90"
        >
          {isPlaying ? <Pause className="nd:size-5 nd:fill-current nd:stroke-0" aria-hidden="true" /> : <Play className="nd:size-5 nd:fill-current nd:stroke-0" aria-hidden="true" />}
        </button>
        <button
          type="button"
          // Раскрыть без мыши: нажатие на название на сенсорном экране; мышью плеер раскрывается и так
          onClick={() => {
            if (lastPointer.current !== 'mouse') setPinned((p) => !p);
          }}
          tabIndex={-1}
          className="nd:flex nd:min-w-0 nd:flex-1 nd:items-center nd:text-left nd:text-[0.8125rem] nd:leading-5 nd:font-medium"
        >
          <PlayingBars playing={isPlaying} />
          <Tooltip label={title} truncated>
            <span className="nd:min-w-0 nd:truncate">{title}</span>
          </Tooltip>
        </button>
        {skips ? (
          <>
            <button type="button" onClick={skips.onPrevious} disabled={!skips.canPrevious} aria-label="Предыдущий трек" className={ICON_BUTTON}>
              <SkipBack className="nd:fill-current" aria-hidden="true" />
            </button>
            <button type="button" onClick={skips.onNext} disabled={!skips.canNext} aria-label="Следующий трек" className={ICON_BUTTON}>
              <SkipForward className="nd:fill-current" aria-hidden="true" />
            </button>
          </>
        ) : null}
        <VolumeControl volume={volume} />
        <button type="button" onClick={onClose} aria-label="Закрыть плеер" className={cn(ICON_BUTTON, ICON_MUTED)}>
          <X aria-hidden="true" />
        </button>
      </div>
      {/* Перемотка выезжает снизу: строка сетки 0fr → 1fr — плеер растёт в высоту, не вширь */}
      <div
        className={cn('nd:grid nd:transition-[grid-template-rows]', EXPAND)}
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="nd:min-h-0 nd:overflow-hidden">
          <Timeline title={title} scrub={scrub} />
        </div>
      </div>
    </div>
  );
}

/** Три лаймовые полоски у названия: бегут, пока играет, на паузе замирают */
function PlayingBars({ playing }: { playing: boolean }) {
  const bar = cn(
    'nd:w-0.5 nd:rounded-full nd:bg-primary nd:animate-[nd-eq_900ms_ease-in-out_infinite] nd:motion-reduce:animate-none nd:motion-reduce:h-1.5',
    !playing && 'nd:[animation-play-state:paused]'
  );
  return (
    <span aria-hidden="true" className="nd:mr-1.5 nd:flex nd:h-2.5 nd:shrink-0 nd:items-end nd:gap-0.5">
      <i className={bar} />
      <i className={cn(bar, 'nd:[animation-delay:-300ms]')} />
      <i className={cn(bar, 'nd:[animation-delay:-600ms]')} />
    </span>
  );
}

/** Перемотка от края до края плеера, время по бокам; видимая дорожка 4 px, под мышью и пока тянут — 6 px и бегунок; цель для мыши — 16 px */
function Timeline({ title, scrub }: { title: string; scrub: AudioBarScrub }) {
  const pct = `${scrub.progressRatio * 100}%`;
  return (
    // Отступ 16 px с обеих сторон: у самого плеера слева 4 px, справа 8 px
    <div className="nd:flex nd:items-center nd:gap-2.5 nd:pt-0.5 nd:pr-2 nd:pb-1.5 nd:pl-3 nd:text-xs nd:text-muted-foreground nd:tabular-nums">
      <span aria-hidden="true">{formatAudioClock(scrub.currentTimeSec)}</span>
      <div
        ref={scrub.barRef}
        role="slider"
        tabIndex={0}
        aria-label={`Позиция: ${title}`}
        aria-valuemin={0}
        aria-valuemax={scrub.ariaValueMax}
        aria-valuenow={scrub.ariaValueNow}
        aria-valuetext={`${formatAudioClock(scrub.currentTimeSec)} из ${formatAudioClock(scrub.durationSec)}`}
        aria-disabled={!scrub.durationKnown}
        onPointerDown={scrub.onTrackPointerDown}
        onPointerMove={scrub.onTrackPointerMove}
        onPointerUp={scrub.onTrackPointerUp}
        onPointerCancel={scrub.onTrackPointerCancel}
        onKeyDown={scrub.onTrackKeyDown}
        data-scrubbing={scrub.isScrubbing ? '' : undefined}
        className={cn(SLIDER, 'nd:h-4 nd:flex-1')}
      >
        <span className={cn(SLIDER_TRACK, 'nd:h-1 nd:w-full nd:transition-[height] nd:group-hover/scrub:h-1.5 nd:group-data-scrubbing/scrub:h-1.5')}>
          <span className={cn(SLIDER_RANGE, 'nd:inset-y-0 nd:left-0')} style={{ width: pct }} />
        </span>
        <span aria-hidden="true" className={cn(SLIDER_THUMB, 'nd:top-1/2 nd:-translate-x-1/2 nd:-translate-y-1/2')} style={{ left: pct }} />
      </div>
      <span aria-hidden="true">{formatAudioClock(scrub.durationSec)}</span>
    </div>
  );
}

function VolumeIcon({ level }: { level: number }): ReactNode {
  if (level <= 0) return <VolumeX aria-hidden="true" />;
  if (level < 0.5) return <Volume1 aria-hidden="true" />;
  return <Volume2 aria-hidden="true" />;
}

/**
 * Громкость: значок — выключить/включить звук; над ним, пока мышь рядом, — вертикальный ползунок во всплывашке, такой же,
 * как перемотка. Колёсико над значком и ползунком — громче и тише (useGlobalAudioVolumeSlider, areaRef).
 */
function VolumeControl({ volume }: { volume: GlobalAudioVolumeSlider }) {
  const pct = Math.round(volume.level * 100);
  return (
    <div ref={volume.areaRef} className="nd:relative nd:shrink-0" onMouseEnter={volume.onEnter} onMouseLeave={volume.onLeave}>
      <div
        aria-hidden={!volume.open}
        className={cn(
          'nd:absolute nd:bottom-full nd:left-1/2 nd:-translate-x-1/2 nd:pb-2 nd:transition-opacity nd:duration-150',
          volume.open ? 'nd:visible nd:opacity-100' : 'nd:pointer-events-none nd:invisible nd:opacity-0'
        )}
      >
        <div className="nd:rounded-2xl nd:bg-popover nd:px-1 nd:py-2.5 nd:shadow-lg nd:shadow-black/25 nd:ring-1 nd:ring-foreground/10">
          <div
            ref={volume.trackRef}
            role="slider"
            tabIndex={0}
            aria-label="Громкость"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-valuetext={`${pct}%`}
            aria-orientation="vertical"
            onPointerDown={volume.onTrackPointerDown}
            onPointerMove={volume.onTrackPointerMove}
            onPointerUp={volume.onTrackPointerEnd}
            onPointerCancel={volume.onTrackPointerEnd}
            onKeyDown={volume.onTrackKeyDown}
            data-scrubbing={volume.dragging ? '' : undefined}
            className={cn(SLIDER, 'nd:h-22 nd:w-4')}
          >
            <span className={cn(SLIDER_TRACK, 'nd:h-full nd:w-1 nd:transition-[width] nd:group-hover/scrub:w-1.5 nd:group-data-scrubbing/scrub:w-1.5')}>
              <span className={cn(SLIDER_RANGE, 'nd:inset-x-0 nd:bottom-0')} style={{ height: `${pct}%` }} />
            </span>
            <span aria-hidden="true" className={cn(SLIDER_THUMB, 'nd:left-1/2 nd:-translate-x-1/2 nd:translate-y-1/2')} style={{ bottom: `${pct}%` }} />
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={volume.toggleMute}
        aria-label={volume.level <= 0 ? 'Включить звук' : 'Выключить звук'}
        aria-pressed={volume.level <= 0}
        className={cn(ICON_BUTTON, ICON_MUTED, volume.open && 'nd:bg-foreground/10 nd:text-foreground')}
      >
        <VolumeIcon level={volume.level} />
      </button>
    </div>
  );
}
